import type { DocumentLineInput, DocumentTotals, WithholdingConfig } from './types';
import { calculateLineTotals } from './calculate-line-totals';

/**
 * Calcule les totaux d'un document (devis, facture, BL, etc.).
 *
 * @param lines - Lignes du document
 * @param clientType - 'company' | 'individual'
 * @param withholding - Config RAS (seuil + taux). Si absent, pas de RAS.
 * @param stampAmount - Timbre fiscal (DT). Exclu de la base de la RAS ; ajouté au net à payer.
 */
export function calculateDocumentTotals(
  lines: DocumentLineInput[],
  clientType: 'company' | 'individual',
  withholding?: WithholdingConfig,
  stampAmount = 0,
): DocumentTotals {
  let totalHt = 0;
  let totalDiscount = 0;
  let totalVat = 0;
  let totalTtc = 0;

  for (const line of lines) {
    const lineTotals = calculateLineTotals(line);
    totalHt += lineTotals.totalHt;
    totalDiscount += lineTotals.discountAmount;
    totalVat += lineTotals.vatAmount;
    totalTtc += lineTotals.totalTtc;
  }

  totalHt = round3(totalHt);
  totalDiscount = round3(totalDiscount);
  totalVat = round3(totalVat);
  totalTtc = round3(totalTtc);

  // RAS : uniquement pour entreprises, au-dessus du seuil.
  // Base = totalTtc SANS le timbre (le timbre n'entre pas dans l'assiette RAS).
  let withholdingAmount = 0;
  if (withholding && clientType === 'company' && totalTtc > withholding.threshold) {
    withholdingAmount = round3(totalTtc * withholding.rate / 100);
  }

  const stamp = round3(stampAmount);
  const netToPay = round3(totalTtc + stamp - withholdingAmount);

  return {
    totalHt,
    totalDiscount,
    totalVat,
    totalTtc,
    withholdingAmount,
    stampAmount: stamp,
    netToPay,
  };
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
